import * as Yup from 'yup';

const requiredField = (fieldName) => Yup.string().required(`${fieldName} is required`);
const emailValidation = Yup.string().email('Invalid email format').required('Email is required');
const phoneValidation = Yup.string().matches(/^[0-9]{10,15}$/, 'Invalid phone number format').required('Phone number is required');
const dateValidation = Yup.date().typeError('Must be a valid date').required('Date is required');
const positiveNumberValidation = Yup.number().typeError('Must be a number').positive('Must be a positive number').required('This field is required');
const booleanValidation = Yup.boolean().required('This field is required');
const optionalString = Yup.string();
const ageValidation = Yup.number()
  .typeError('Age must be a number')
  .min(0, 'Age cannot be negative')
  .max(1800, 'Age must be reasonable')
  .required('Age is required');

/**
 * Helper function to check if a value is effectively empty
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is null, undefined, or empty string (after trimming)
 */
const isEffectivelyEmpty = (value) => {
  return value == null || (typeof value === 'string' && value.trim() === '');
};

/**
 * Creates a validation schema based on the provided tenant ID and validates documents
 * @param {string} tenantId - The ID of the tenant for whom signature and date are required
 * @returns {Object} - The Yup validation schema
 */
const createValidationSchema = (tenantId) => {
  const currentTenantId = tenantId ? String(tenantId) : '';

  const attachedDocumentsValidation = Yup.array().of(
    Yup.object().shape({
      id: requiredField('Document ID'),
      name: requiredField('Document name'),
      urls: Yup.array().of(
        Yup.object().shape({
          file_url: Yup.string().required('File URL is required'),
          uploaded_by: Yup.string().default('Default Document'),
          uploaded_at: Yup.date().typeError('Must be a valid date').default(() => new Date()),
        })
      )
        .required('URLs array is required')
        .min(1, 'At least one URL is required')
        .test({
          name: 'fileUploadRequiredCheck',
          message: 'Upload is required for this document',
          test: function (value, context) {
            const documentName = context.parent?.name || 'Unknown Document';
            const isValid = value?.every(doc => doc.file_url !== 'upload_requested');
            if (!isValid) {
              return this.createError({
                message: `Upload is required for this document: "${documentName}"`,
                path: context.path,
              });
            }
            return true;
          }
        }),
      send_to_users: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required('User ID is required'),
        })
      ).required('Send to users array is required'),
      signature_required_from: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required('User ID is required'),
        })
      ).required('Signature required from array is required'),
      signatures: Yup.array().when('signature_required_from', {
        is: (signatureRequiredFrom) => Array.isArray(signatureRequiredFrom) && signatureRequiredFrom.length > 0,
        then: (schema) => schema.of(
          Yup.object().shape({
            id: requiredField('Signature ID'),
            name: requiredField('Signature name'),
            signature: Yup.string().test({
              name: 'signatureForCurrentTenant',
              test: function (value, context) {
                const signatureId = context.parent?.id;
                const tenantName = context.parent?.name || 'Unknown Document';
                if (String(signatureId) !== currentTenantId) {
                  return true;
                }
                if (isEffectivelyEmpty(value)) {
                  return this.createError({
                    message: `Signature is required from tenant ${tenantName} in document`,
                    path: context.path,
                  });
                }
                return true;
              },
            }),
            date: Yup.string().test({
              name: 'dateForCurrentTenant',
              test: function (value, context) {
                const signatureId = context.parent?.id;
                const tenantName = context.parent?.name || 'Unknown Document';
                if (String(signatureId) !== currentTenantId) {
                  return true;
                }
                if (isEffectivelyEmpty(value)) {
                  return this.createError({
                    message: `Date is required from tenant ${tenantName} in document`,
                    path: context.path,
                  });
                }
                const parsedDate = new Date(value);
                if (isNaN(parsedDate.getTime())) {
                  return this.createError({
                    message: `Valid date is required for tenant ${tenantName} in document`,
                    path: context.path,
                  });
                }
                return true;
              },
            }),
          })
        )
          .test({
            name: 'currentTenantMustSign',
            test: function (signatures, context) {
              const documentName = context.parent?.name || 'Unknown Document';
              const signatureRequiredFrom = context.parent?.signature_required_from || [];
              const currentTenantMustSign = signatureRequiredFrom.some(req => String(req.id) === currentTenantId);
              if (!currentTenantMustSign) {
                return true;
              }
              if (!signatures || signatures.length === 0) {
                return this.createError({
                  message: `Document "${documentName}" requires signature from tenant ${currentTenantId} but no signatures are provided`,
                  path: context.path,
                });
              }
              const currentTenantSignature = signatures.find(sig => String(sig.id) === currentTenantId);
              if (!currentTenantSignature) {
                return this.createError({
                  message: `Tenant ${currentTenantId} must provide signature for document "${documentName}"`,
                  path: context.path,
                });
              }
              const missingFields = [];
              if (isEffectivelyEmpty(currentTenantSignature.signature)) {
                missingFields.push('signature');
              }
              if (isEffectivelyEmpty(currentTenantSignature.date)) {
                missingFields.push('date');
              }
              if (missingFields.length > 0) {
                return this.createError({
                  message: `Missing ${missingFields.join(' and ')} for tenant ${currentTenantId} in document "${documentName}"`,
                  path: context.path,
                });
              }
              return true;
            },
          })
          .min(1, 'At least one signature is required for documents requiring signatures'),
        otherwise: (schema) => schema.of(
          Yup.object().shape({
            id: requiredField('Signature ID'),
            name: requiredField('Signature name'),
            signature: Yup.string().notRequired(),
            date: Yup.string().notRequired(),
          })
        ).notRequired(),
      }),
    })
  ).required('Attached documents are required');

  return Yup.object().shape({
    property_id: Yup.string().required('Property ID is required'),
    property_details: Yup.object().shape({
      logo: optionalString,
      partnership_label: requiredField('Partnership Label'),
      landlord: requiredField('Landlord name'),
      utilities_available: Yup.object().shape({
        electricity: booleanValidation,
        heat: booleanValidation,
        water_waste: booleanValidation,
      }).required('Utilities information is required'),
      allowed_pets: Yup.object().shape({
        dogs: Yup.boolean().test(
          'dogs-required-if-dog-pets',
          'Dogs must be allowed when dog pets are added',
          function (value) {
            const rootData = this.options.from?.[this.options.from.length - 1]?.value || {};
            const pets = rootData?.lease_details?.pets || [];
            const hasDogs = pets.some(pet =>
              pet.category && pet.category.toLowerCase().includes('dog')
            );
            return !hasDogs || value === true;
          }
        ),
        cats: Yup.boolean().test(
          'cats-required-if-cat-pets',
          'Cats must be allowed when cat pets are added',
          function (value) {
            const rootData = this.options.from?.[this.options.from.length - 1]?.value || {};
            const pets = rootData?.lease_details?.pets || [];
            const hasCats = pets.some(pet =>
              pet.category && pet.category.toLowerCase().includes('cat')
            );
            return !hasCats || value === true;
          }
        ),
        others: Yup.boolean().test(
          'others-required-if-other-pets',
          'Other pets must be allowed when other pet types are added',
          function (value) {
            const rootData = this.options.from?.[this.options.from.length - 1]?.value || {};
            const pets = rootData?.lease_details?.pets || [];
            const hasOthers = pets.some(pet =>
              pet.category &&
              !pet.category.toLowerCase().includes('dog') &&
              !pet.category.toLowerCase().includes('cat') &&
              pet.category.trim() !== ''
            );
            return !hasOthers || value === true;
          }
        ),
      }).required('Allowed pets information is required'),
      pet_fees: Yup.object().shape({
        cat_fee: Yup.string().required('Cat fee is required'),
        dog_fee: Yup.string().required('Dog fee is required'),
        other_fee: Yup.string().required('Other pet fee is required'),
      }).required('Pet fees information is required'),
      property_name: requiredField('Property name'),
      building_name: requiredField('Building name'),
      unit_no: requiredField('Unit number'),
      address: requiredField('Address'),
      city: requiredField('City'),
      province: requiredField('Province'),
      postal_code: requiredField('Postal code'),
      incentives: optionalString,
      appliances: Yup.array()
        .of(Yup.string().required('Each appliance must be a string'))
        .min(1, 'At least one appliance is required')
        .required('Appliances are required'),
    }).required('Property details are required'),
    manager: Yup.object().shape({
      first_name: requiredField('Manager first name'),
      last_name: requiredField('Manager last name'),
      phone_no: phoneValidation,
      email: emailValidation,
      signature: optionalString,
      date: Yup.string().notRequired(),
    }).required('Manager information is required'),
    tenants: Yup.array().of(
      Yup.object().shape({
        id: requiredField('Tenant ID'),
        name: requiredField('Tenant name'),
        email: emailValidation,
        signature: Yup.string().test({
          name: 'tenantSignatureValidation',
          test: function (value, context) {
            const tenantId = context.parent.id;
            if (String(tenantId) !== currentTenantId) {
              return true;
            }
            if (isEffectivelyEmpty(value)) {
              return this.createError({
                message: `Signature is required for tenant ${currentTenantId}`,
                path: context.path,
              });
            }
            return true;
          },
        }),
        date: Yup.string().test({
          name: 'tenantDateValidation',
          test: function (value, context) {
            const tenantId = context.parent.id;
            if (String(tenantId) !== currentTenantId) {
              return true;
            }
            if (isEffectivelyEmpty(value)) {
              return this.createError({
                message: `Date is required for tenant ${currentTenantId}`,
                path: context.path,
              });
            }
            const parsedDate = new Date(value);
            if (isNaN(parsedDate.getTime())) {
              return this.createError({
                message: `Valid date is required for tenant ${currentTenantId}`,
                path: context.path,
              });
            }
            return true;
          },
        }),
      })
    ).min(1, 'At least one tenant is required').required('Tenants information is required'),
    occupants: Yup.array().of(
      Yup.object().shape({
        name: requiredField('Occupant name'),
        relationship: requiredField('Relationship to tenant'),
        age: ageValidation,
      })
    ).notRequired(),
    lease_details: Yup.object().shape({
      start_date: dateValidation,
      end_date: dateValidation.min(
        Yup.ref('start_date'),
        'End date cannot be before start date'
      ),
      basic_rent: positiveNumberValidation,
      pets: Yup.array().of(
        Yup.object().shape({
          category: requiredField('Pet category'),
          total_fee: Yup.number()
            .typeError('Total Fee must be a number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Total Fee cannot be negative. Make sure you clicked on Done to save your changes.')
            .test(
              'fee-required-if-category',
              'Total Fee per pet is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const category = this.parent.category;
                return !category || category.length === 0 || value !== undefined;
              }
            ),
          no_of_pets: Yup.number()
            .typeError('Number must be a whole number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Cannot be negative. Make sure you clicked on Done to save your changes.')
            .integer('Must be a whole number. Make sure you clicked on Done to save your changes.')
            .test(
              'number-required-if-category',
              'Number of pets is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const category = this.parent.category;
                return !category || category.length === 0 || value !== undefined;
              }
            ),
        })
      ).test(
        'not-empty-objects',
        'Make sure you have entered the data in all fields and clicked on Done to save your changes.',
        (arr) => {
          if (!arr) return true;
          return arr.every((entry) =>
            entry.category || entry.total_fee !== undefined || entry.no_of_pets !== undefined
          );
        }
      ),
      parking: Yup.array().of(
        Yup.object().shape({
          parking_type: requiredField('Parking type'),
          total_fee: Yup.number()
            .typeError('Total Fee must be a number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Total Fee cannot be negative. Make sure you clicked on Done to save your changes.')
            .test(
              'fee-required-if-parking_type',
              'Total Fee per parking slot is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const parkingType = this.parent.parking_type;
                return !parkingType || parkingType.length === 0 || value !== undefined;
              }
            ),
          no_of_slots: Yup.number()
            .typeError('Number must be a whole number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Cannot be negative. Make sure you clicked on Done to save your changes.')
            .integer('Must be a whole number. Make sure you clicked on Done to save your changes.')
            .test(
              'number-required-if-parking_type',
              'Number of parking slots is required. Make sure you clicked on Done to save your changes.',
              function (value) {
                const parkingType = this.parent.parking_type;
                return !parkingType || parkingType.length === 0 || value !== undefined;
              }
            ),
        })
      ).test(
        'not-empty-objects',
        'Make sure you have entered the data in all fields and clicked on Done to save your changes.',
        (arr) => {
          if (!arr) return true;
          return arr.every((entry) =>
            entry.parking_type || entry.total_fee !== undefined || entry.no_of_slots !== undefined
          );
        }
      ),
      storage: Yup.array().of(
        Yup.object().shape({
          total_fee: Yup.number()
            .typeError('Storage Fee must be a number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Storage Fee cannot be negative. Make sure you clicked on Done to save your changes.')
            .required('Storage Fee per storage unit is required. Make sure you clicked on Done to save your changes.'),
          no_of_slots: Yup.number()
            .typeError('Number must be a whole number. Make sure you clicked on Done to save your changes.')
            .min(0, 'Cannot be negative. Make sure you clicked on Done to save your changes.')
            .integer('Must be a whole number. Make sure you clicked on Done to save your changes.')
            .required('Number of storage slots is required. Make sure you clicked on Done to save your changes.'),
        })
      ).test(
        'not-empty-objects',
        'Make sure you have entered the data in all fields and clicked on Done to save your changes.',
        (arr) => {
          if (!arr) return true;
          return arr.every((entry) =>
            entry.total_fee !== undefined || entry.no_of_slots !== undefined
          );
        }
      ),
      pro_ration: Yup.object().shape({
        date_of_early_move_in: optionalString,
        amount_applicable: Yup.number()
          .typeError('Amount Applicable must be a number')
          .min(0, 'Amount Applicable cannot be negative')
          .test('amount-applicable-required-if-date', 'Amount Applicable is required', function (value) {
            const date = this.parent.date_of_early_move_in;
            return !date || date.length === 0 || value !== undefined;
          }),
        amount_to_be_charged: Yup.number()
          .typeError('Amount to be Charged must be a number')
          .min(0, 'Amount to be Charged cannot be negative')
          .test('amount-to-be-charged-required-if-date', 'Amount to be Charged is required', function (value) {
            const date = this.parent.date_of_early_move_in;
            return !date || date.length === 0 || value !== undefined;
          }),
      }).notRequired(),
      telus: Yup.object().shape({
        included: Yup.boolean().notRequired(),
        charges: Yup.number()
          .typeError('Charges must be a number')
          .min(0, 'Charges cannot be negative')
          .test('charges-required-if-included', 'Telus charges are required', function (value) {
            const included = this.parent.included;
            return !included || value !== undefined;
          }),
      }).notRequired(),
      incentives: optionalString,
    }).required('Lease details are required'),
    security_deposits: Yup.object().shape({
      refundable: Yup.string().notRequired(),
      non_refundable: Yup.string().notRequired(),
    }).notRequired(),
    attached_documents: attachedDocumentsValidation,
    additional_notes: optionalString,
    lease_status: Yup.object().shape({
      state: Yup.string().required('Lease status state is required'),
      effective_date: Yup.date().typeError('Must be a valid date').required('Effective date is required'),
      end_reason: Yup.string().notRequired(),
      notes: Yup.string().notRequired(),
    }).required('Lease status is required'),
    isActive: booleanValidation,
    isInprogress: Yup.boolean().required('isInprogress is required'),
  });
};

export const getValidationSchema = createValidationSchema;